import { normalizeEmailContent } from "../blocks/row/normalize-content";
import type {
  EmailCellContent,
  EmailRowNode,
  EmailTopLevelNode,
  TiptapEmailJSON,
} from "../types/tiptap-json";
import type { JSONContent } from "@tiptap/react";

/** Wraps a content node in a full-width (colSpan=4) row. */
export function wrapInRow(node: EmailCellContent): EmailRowNode {
  return {
    type: "emailRow",
    content: [{ type: "emailCell", attrs: { colSpan: 4 }, content: [node] }],
  };
}

/** Full-width row containing an empty paragraph — used as a spacer. */
export const SPACER_ROW: EmailRowNode = wrapInRow({
  type: "emailParagraph",
  attrs: { textAlign: "left" },
});

export interface DefaultHeaderInfo {
  clinicName?: string;
  logoUrl?: string;
}

/**
 * Builds a default header row: clinic name (H1, left) + logo (right).
 */
function buildDefaultHeader({
  clinicName,
  logoUrl,
}: DefaultHeaderInfo): EmailRowNode {
  const nameCell = {
    type: "emailCell" as const,
    attrs: { colSpan: 2 as const },
    content: [
      {
        type: "emailHeading" as const,
        attrs: { level: 1 as const, textAlign: "left" as const },
        ...(clinicName
          ? { content: [{ type: "text" as const, text: clinicName }] }
          : {}),
      },
    ],
  };

  const logoCell = {
    type: "emailCell" as const,
    attrs: { colSpan: 2 as const },
    content: logoUrl
      ? [
          {
            type: "emailImage" as const,
            attrs: {
              src: logoUrl,
              alt: "Clinic Logo",
              width: "auto",
              alignment: "right" as const,
              maxHeight: "65px",
            },
          },
        ]
      : [
          {
            type: "emailParagraph" as const,
            attrs: { textAlign: "right" as const },
          },
        ],
  };

  return { type: "emailRow", content: [nameCell, logoCell] };
}

/**
 * Merges an email header template with body content into a single document.
 *
 * When no header is set, a default header with the clinic name and logo is used.
 * The header JSON may be in legacy flat format, so we normalize it first
 * to ensure all nodes are wrapped in rows/cells.
 */
export function mergeHeaderWithBody(
  headerJson: JSONContent | null | undefined,
  bodyNodes: EmailTopLevelNode[],
  defaultHeader?: DefaultHeaderInfo,
): TiptapEmailJSON {
  if (!headerJson?.content?.length) {
    const header = buildDefaultHeader(defaultHeader ?? {});
    return { type: "doc", content: [header, SPACER_ROW, ...bodyNodes] };
  }

  const normalized = normalizeEmailContent(headerJson);
  const headerNodes = (normalized?.content ?? []) as EmailTopLevelNode[];

  return {
    type: "doc",
    content: [...headerNodes, SPACER_ROW, ...bodyNodes],
  };
}
